import { useEffect, useState, useCallback } from 'react';
import { Parse } from '../lib/parse';

const DEFAULT_BRANDING = {
  appName: 'DANAsign',
  headerBarColor: '#071e46',
  primaryColor: '#071e46',
  secondaryColor: '#d1d5db',
  loginLogo: '',
  headerLogo: '',
  faviconUrl: '/favicon.ico',
  documentationUrl: '',
  isDefault: true
};

export const useBrandingConfig = (tenantId = null) => {
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBrandingConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('useBrandingConfig: Fetching with tenantId:', tenantId);
      const config = await Parse.Cloud.run('getbrandingconfig', {
        tenantId: tenantId || ''
      });

      console.log('useBrandingConfig: Received config:', config);
      
      // If we got default branding and have a tenantId, try to initialize
      if (config?.isDefault && tenantId) {
        console.log('useBrandingConfig: Got default branding, attempting to initialize');
        try {
          await Parse.Cloud.run('initializebrandingconfig', {});
          // Fetch again after initialization
          const newConfig = await Parse.Cloud.run('getbrandingconfig', {
            tenantId: tenantId || ''
          });
          setBranding(newConfig || DEFAULT_BRANDING);
        } catch (initError) {
          console.log('useBrandingConfig: Initialization failed, using default:', initError?.message);
          setBranding(config || DEFAULT_BRANDING);
        }
      } else {
        setBranding(config || DEFAULT_BRANDING);
      }

      // Apply favicon if available
      const effectiveConfig = config?.isDefault ? (config || DEFAULT_BRANDING) : config;
      if (effectiveConfig?.faviconUrl) {
        const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
        link.rel = 'icon';
        link.href = effectiveConfig.faviconUrl;
        if (!document.querySelector("link[rel~='icon']")) {
          document.head.appendChild(link);
        }
      }

      // Apply document title
      if (effectiveConfig?.appName) {
        document.title = effectiveConfig.appName;
      }

      // Apply CSS variables for colors
      const headerColor = config?.headerBarColor || DEFAULT_BRANDING.headerBarColor;
      const primaryColor = config?.primaryColor || DEFAULT_BRANDING.primaryColor;
      const secondaryColor = config?.secondaryColor || DEFAULT_BRANDING.secondaryColor;
      
      console.log('useBrandingConfig: Applying CSS variables:', { headerColor, primaryColor, secondaryColor });
      
      document.documentElement.style.setProperty('--header-bar-color', headerColor);
      document.documentElement.style.setProperty('--primary-color', primaryColor);
      document.documentElement.style.setProperty('--secondary-color', secondaryColor);
      document.documentElement.style.setProperty('--app-brand', primaryColor);
      document.documentElement.style.setProperty('--app-brand-dark', primaryColor);
    } catch (err) {
      console.error('Error fetching branding config:', err);
      setError(err?.message || 'Failed to fetch branding config');
      setBranding(DEFAULT_BRANDING);
    } finally {
      setIsLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchBrandingConfig();
  }, [fetchBrandingConfig]);

  return {
    branding,
    isLoading,
    error,
    refetch: fetchBrandingConfig
  };
};
